/**
 * Qué te cuesta cada colaborador.
 *
 * El dato es REAL: OpenRouter devuelve el coste de cada llamada (`usage.include`) y el motor lo
 * guarda en su telemetría. Ningún competidor enseña esto porque casi ninguno lo tiene: cuando el
 * proveedor de IA eres tú, el coste del cliente es una cifra que prefieres no mirar.
 *
 * Aquí es al revés: el autor paga su consumo con su propia clave, así que enseñárselo desglosado
 * no es transparencia decorativa — es la factura que ya está pagando.
 */
import type { Telemetria } from "../api";
import { dolares } from "../api";

export default function Gasto({ t, nombres }: { t: Telemetria; nombres: Record<string, string> }) {
  const filas = Object.entries(t.byAgent)
    .map(([id, u]) => ({ id, nombre: nombres[id] ?? id, coste: u.cost, llamadas: u.calls }))
    .filter((f) => f.coste > 0)
    .sort((a, b) => b.coste - a.coste)
    .slice(0, 6);

  if (!filas.length) {
    return <p className="vacio">Todavía no has usado a nadie. En cuanto trabajes, aquí verás lo que cuesta cada oficio.</p>;
  }

  const max = filas[0].coste;
  const alto = 14, hueco = 6.5;

  return (
    <svg viewBox={`0 0 300 ${filas.length * (alto + hueco) + 6}`} role="img"
         aria-label="Gasto por colaborador, en dólares">
        {/* El oro de la marca es un DEGRADADO —#FFD95A → #F2A60C, el mismo de la web y del logo—,
            no un plano. Los hex van literales: en un <linearGradient> las variables CSS no se
            resuelven de forma fiable en todos los navegadores, y una barra sin color no se ve. */}
        <defs>
          <linearGradient id="oroBarra" gradientUnits="userSpaceOnUse" x1="108" y1="0" x2="258" y2="0">
            <stop offset="0%" stopColor="#FFE9A0" />
            <stop offset="55%" stopColor="#FFC400" />
            <stop offset="100%" stopColor="#E08A00" />
          </linearGradient>
        </defs>
      {filas.map((f, i) => {
        const y = 6 + i * (alto + hueco);
        const w = Math.max(3, 150 * (f.coste / max));
        return (
          <g key={f.id}>
            <text x="0" y={y + 10} className="eje">
              {f.nombre.length > 17 ? f.nombre.slice(0, 16) + "…" : f.nombre}
            </text>
            <rect x="108" y={y} width={w} height={alto} rx="4" fill="url(#oroBarra)" className="barra">
              <title>{`${f.nombre}: ${dolares(f.coste)} en ${f.llamadas} llamadas`}</title>
            </rect>
            <text x="300" y={y + 10} className="eje" textAnchor="end">{dolares(f.coste)}</text>
          </g>
        );
      })}
    </svg>
  );
}
